import React, { Suspense, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { cva } from 'class-variance-authority';
import styles from './router.module.css';
import { AppProvider } from './provider';
import { subscribePostHogPageview } from '@/lib/analytics/posthog-pageview';

const suspenseFallback = cva(styles.suspenseFallback);
import {
  createRouter,
  createRoute,
  createRootRoute,
  RouterProvider,
  createBrowserHistory,
  redirect,
  Outlet,
  useParams,
} from '@tanstack/react-router';
import { sessionOpenTab } from '@/session/session-bridge';
import { RouterBridge } from '@/app/boot/router-bridge';
import { DeepLinkBridge } from '@/app/boot/deep-link-bridge';
import { FocusDocBridge } from '@/app/boot/focus-doc-bridge';
import { LiveTranscriptBridge } from '@/app/boot/live-transcript-bridge';
import { InvitationBridge } from '@/app/boot/invitation-bridge';
import { MembershipBridge } from '@/app/boot/membership-bridge';
import { ReferralBridge } from '@/app/boot/referral-bridge';
import { IntercomBridge } from '@/app/boot/intercom-bridge';
import { App } from '@/app/app';
import { BootGate } from '@/app/boot/boot-gate';
import { CompanionLayer } from '@/features/companion/components/companion-layer';

import { InvitationAccept } from './routes/invitation-accept';
import { JoinByToken } from './routes/join-by-token';
import { RedeemReferral } from './routes/redeem-referral';
import { AuthCallback } from './routes/auth-callback';
import { AuthDesktop } from './routes/auth-desktop';
import { CalendarConnected } from './routes/calendar-connected';
import { AppErrorBoundary } from '@/app/boot/app-error-boundary';
import { SyncSystemProvider } from '@/lib/sync/sync-system-context';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { useSessionResume } from '@/lib/auth/use-session-resume';
import { refreshAccessToken } from '@/lib/auth/auth0';
import { applyRefreshedTokens, getRefreshToken } from '@/lib/auth/token-storage';
import { configureTokenRefresh } from '@/lib/auth/token-refresh-scheduler';
import { configureAuth } from '@/lib/http/client';
import { resolveEntityKind } from '@/lib/sync/entity-store';
import { SessionWrapper, WorkspaceSubscriber, AuthSyncBridge } from '@/lib/sync/sync-bridges';
import { WorkspaceServiceProvider } from '@/lib/workspace/workspace-service-provider';
import { SnapshotServiceProvider } from '@/lib/snapshot/snapshot-service-provider';
import { CanvasPreviewProvider } from '@/lib/canvas/canvas-preview-context';
import { CanvasViewerPreview } from '@/features/canvas/components/canvas-viewer.preview';
import { DocumentViewerProvider } from '@/lib/documents/document-viewer-context';
import { EntityDocumentViewer } from '@/features/editor/components/rich-text-viewer';
import { DocumentEditorProvider } from '@/lib/documents/document-editor-context';
import { MarkdownPreviewProvider } from '@/lib/documents/markdown-preview-context';
import { MarkdownSectionPreview } from '@/features/editor/components/markdown-section-preview';
import { SurfaceOrbProvider } from '@/lib/documents/surface-orb-context';
import { RichTextEditor } from '@/features/editor/components/rich-text-editor';
import { SurfaceMount } from '@/features/document-surfaces/components/surface-mount';
import { SpeakerLabelProvider } from '@/lib/recordings/speaker-label-context';
import { SpeakerLabelEdit } from '@/features/recordings/components/speaker-label-edit';
import { AiComposerProvider } from '@/lib/ai-composer/ai-composer-context';
import { EditActionTransportProvider } from '@/lib/ai-composer/edit-action-transport-context';
import { openEditActionStream } from '@/lib/ai-composer/edit-action-protocol';
import { useSyncTransport } from '@/lib/sync/sync-system-context';
import { Spin, Flex } from '@nula/core-ui';
import { useSessionStateOptional } from '@/session';
import type { TabType } from '@/session/types';
import { kindToTabType } from '@/hooks/navigation';
import { useTheme } from '@/hooks/use-theme';
import { useEntityStore } from '@/lib/sync/entity-store';
import { useYjsRegistry } from '@/lib/sync/hooks/yjs-registry-context';
import { isNative } from '@/lib/platform';
import { RecordingController } from '@/features/recordings/components/recording-controller';
const LazyShareLinkViewer = React.lazy(() =>
  import('@/app/routes/share/share-link-viewer').then((m) => ({ default: m.ShareLinkViewer })),
);

// Configure apiFetch to read token from auth store — works on all platforms
configureAuth(() => useAuthStore.getState().accessToken);

// Wire the proactive token-refresh scheduler (LUMEN-4E): refresh the access
// token before it expires and re-push it to the native calendar/recording
// clients, so a long-lived desktop session never runs on a stale token.
configureTokenRefresh({
  getRefreshToken,
  refreshAccessToken,
  applyTokens: applyRefreshedTokens,
});

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3000/ws/sync';

// ---------------------------------------------------------------------------
// WorkspaceBootstrap — runs ABOVE SyncSystemProvider.
//
// When the auth token resolves, populates useWorkspaceStore with the user's
// workspace list (and selects the personal workspace by default). This must
// happen before SyncSystemProvider mounts so that activeWorkspaceId is set
// when the provider's useMemo runs setActiveEntityStore().
//
// Also handles the /api/auth/me round-trip for user profile sync. Both
// requests fire in parallel; logout-on-token-clear is handled here too via
// the absence of token (no transport to disconnect at this layer).
// ---------------------------------------------------------------------------

function WorkspaceBootstrap() {
  const token = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (!token) return;

    const abortController = new AbortController();

    // /api/auth/me — sync user profile fields (email/name/picture).
    // personalWorkspaceId is no longer written here; useWorkspaceStore
    // is now the source of truth (populated by refreshFromServer below).
    void fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: abortController.signal,
    })
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        // The session may have changed (logout, or a different user signed
        // in) while this request was in flight — a stale response must not
        // overwrite the current session's identity.
        if (useAuthStore.getState().accessToken !== token) return;
        const current = useAuthStore.getState().user;
        // Partial state patch for Zustand's setState (shallow-merged).
        const update: Partial<ReturnType<typeof useAuthStore.getState>> = {
          // onboardingCompletedAt: null = not onboarded; string = done; undefined = unknown.
          onboardingCompletedAt: data.onboardingCompletedAt ?? null,
          nulaUserId: data.userId ?? null,
          intercom: data.intercom ?? null,
        };
        if (current) {
          update.user = {
            ...current,
            email: data.email || current.email,
            name: data.name || current.name,
            picture: data.avatarUrl || current.picture,
          };
        }
        useAuthStore.setState(update);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.warn('[WorkspaceBootstrap] Failed to sync API user profile');
      });

    // GET /api/workspaces — populates the selection store so the gate below
    // (in MainLayout) can mount SyncSystemProvider with a real workspaceId.
    void useWorkspaceStore.getState().refreshFromServer()
      .catch((err) => {
        console.error('[WorkspaceBootstrap] refreshFromServer failed', err);
        Sentry.captureException(err);
      });

    return () => abortController.abort();
  }, [token]);

  return null;
}

// ---------------------------------------------------------------------------
// resolveRealTabType — pure probe for a tab id's real entity kind.
//
// Cold deep-link URLs always say 'document', but the entity may actually be
// any surviving kind. Probe each per-kind Map (documents carry their kind on
// the row; other kinds live in their own Map) and fall back to the tab's
// declared type when the id isn't in the store yet.
// ---------------------------------------------------------------------------

interface RealTypeMaps {
  entityMeta: Map<string, { kind: TabType }>;
  recordings: Map<string, unknown>;
  canvases: Map<string, unknown>;
  spaces: Map<string, unknown>;
  collections: Map<string, unknown>;
}

export function resolveRealTabType(maps: RealTypeMaps, tabId: string, fallback: TabType): TabType {
  const docMetaEntry = maps.entityMeta.get(tabId);
  if (docMetaEntry) return docMetaEntry.kind;
  if (maps.recordings.has(tabId)) return 'recording';
  if (maps.canvases.has(tabId)) return 'canvas';
  if (maps.spaces.has(tabId)) return 'space';
  if (maps.collections.has(tabId)) return 'collection';
  return fallback;
}

// ---------------------------------------------------------------------------
// SessionPruner — removes tabs whose docs/collections no longer exist
// ---------------------------------------------------------------------------

function SessionPruner() {
  const session = useSessionStateOptional();
  const validateAndPrune = session?.validateAndPrune;
  const cursor = useEntityStore((s) => s.cursor);
  const entityMeta = useEntityStore((s) => s.entityMeta);
  const collections = useEntityStore((s) => s.collections);
  const recordings = useEntityStore((s) => s.recordings);
  const canvases = useEntityStore((s) => s.canvases);
  const spaces = useEntityStore((s) => s.spaces);
  const conversations = useEntityStore((s) => s.conversations);

  useEffect(() => {
    // Only prune after the first sync has completed (cursor is set).
    // Before that, the entity store is empty and we'd incorrectly
    // close every tab.
    if (!cursor || !validateAndPrune) return;

    const existingIds = new Set<string>();
    for (const id of entityMeta.keys()) existingIds.add(id);
    for (const id of collections.keys()) existingIds.add(id);
    for (const id of recordings.keys()) existingIds.add(id);
    for (const id of canvases.keys()) existingIds.add(id);
    for (const id of spaces.keys()) existingIds.add(id);
    for (const id of conversations.keys()) existingIds.add(id);
    validateAndPrune(existingIds);
  }, [cursor, entityMeta, collections, recordings, canvases, spaces, conversations, validateAndPrune]);

  return null;
}

// ---------------------------------------------------------------------------
// EntitySubscriber — single source of truth for Yjs doc subscriptions.
//
// Subscribes every open non-view tab whenever session state or entityMeta changes.
// This covers two cases:
//   1. Cold deep-link boot: tab is in session state before the editor mounts,
//      so the subscription (and type correction from entityMeta) happens here first.
//   2. Normal tab open: editor's useEntityYjs also calls registry.subscribe —
//      that's fine, registry.subscribe is idempotent (increments refCount).
//
// Long-term this could become the sole subscription site by removing the
// registry.subscribe call from useEntityYjs, but that's a larger refactor.
// For now both paths are safe and additive.
// ---------------------------------------------------------------------------

function EntitySubscriber() {
  const session = useSessionStateOptional();
  const state = session?.state ?? null;
  const openTab = session?.openTab;
  const registry = useYjsRegistry();
  const entityMeta = useEntityStore((s) => s.entityMeta);
  const recordings = useEntityStore((s) => s.recordings);
  const collections = useEntityStore((s) => s.collections);
  const canvases = useEntityStore((s) => s.canvases);
  const spaces = useEntityStore((s) => s.spaces);

  useEffect(() => {
    if (!state || !openTab) return;
    for (const panel of Object.values(state.panels)) {
      for (const tab of panel.tabs) {
        if (tab.type === 'view' || tab.type === 'conversation') continue;
        // Resolve real type from entity store — cold deep-link URLs always say
        // 'document' but the entity may actually be any of the surviving kinds.
        // Correct the tab type and subscribe with the real kind.
        const realType = resolveRealTabType(
          { entityMeta, recordings, canvases, spaces, collections },
          tab.id,
          tab.type,
        );
        if (realType !== tab.type) {
          openTab(tab.id, realType);
        }
        void registry.subscribe(tab.id, realType as import('@/lib/sync/yjs-registry').YjsRoomKind);
      }
    }
  }, [state, openTab, registry, entityMeta, recordings, collections, canvases, spaces]);

  return null;
}

// ---------------------------------------------------------------------------
// SharedRoute — renders the share link viewer with lazy loading
// ---------------------------------------------------------------------------

function SharedRoute() {
  const { token } = useParams({ from: '/shared/$token' });
  if (!token) return null;
  return (
    <div data-testid="shared-route" data-token={token}>
      <Suspense fallback={<Flex align="center" justify="center" className={suspenseFallback()}><Spin size="lg" /></Flex>}>
        <LazyShareLinkViewer token={token} />
      </Suspense>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Layout components
// ---------------------------------------------------------------------------

function RootLayout() {
  // Mount the theme engine at the route root so it runs on EVERY screen —
  // login, auth callbacks, share, and the authenticated app alike. useTheme
  // applies the resolved theme to the DOM, persists changes, and hosts the OS
  // color-scheme listener that keeps 'system' mode live. Mounting it here (not
  // in App) ensures pre-auth screens like login also follow the system theme.
  useTheme();

  return (
    <AppErrorBoundary>
      <Outlet />
    </AppErrorBoundary>
  );
}

// Main layout: wraps authenticated app routes with sync providers

// Mounts once above SessionWrapper so session-resume never re-fires when
// SessionProvider mounts/unmounts during the isAuthLoading transition.
//
// Reads the stored refresh_token on boot (tauri-plugin-store on desktop,
// sessionStorage on web) and hydrates the auth store, so reloading or
// reopening doesn't force a re-OAuth round-trip.
function AuthInitializer() {
  useSessionResume();
  return null;
}

/**
 * Bridges SyncTransport → EditActionTransportProvider.
 *
 * Must be mounted inside SyncSystemProvider so useSyncTransport() resolves.
 * Provides EditActionTransportContext to all descendants so useEditAction()
 * can open edit-action streams without requiring deps injection at each call site.
 */
function EditActionTransportBridge({ children }: { children: React.ReactNode }) {
  const syncTransport = useSyncTransport();
  const transport = React.useMemo(
    () => ({
      openEditActionStream: (runId: string, req: import('@/lib/ai-composer/types').EditRequest, blockContent: string) =>
        openEditActionStream(syncTransport, runId, req, blockContent),
    }),
    [syncTransport],
  );
  return (
    <EditActionTransportProvider transport={transport}>
      {children}
    </EditActionTransportProvider>
  );
}

function MainLayout() {
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);

  // Boot is now a single state machine — see apps/lumen/src/app/bootState.ts
  // and boot-state-machine-design.md. <BootGate> switches on the state and
  // renders Splash / Login / WorkspaceSettingUp / OnboardingFlow / children.
  // The full provider stack lives inside BootGate's 'ready' branch, which
  // means activeWorkspaceId is guaranteed non-null whenever the providers
  // mount. The previous ad-hoc `activeWorkspaceId ? <Stack> : <Outlet />`
  // conditional was racy: App's auth gate opened before WorkspaceBootstrap
  // resolved, so the bare <Outlet /> branch rendered the shell (Sidebar
  // etc.) without WorkspaceServiceProvider, throwing at render time.
  //
  // AuthInitializer + WorkspaceBootstrap run unconditionally above BootGate
  // because they drive the state machine: AuthInitializer hydrates auth on
  // boot, WorkspaceBootstrap fetches /api/workspaces when a token appears.
  //
  // SyncSystemProvider is keyed on workspaceId so a workspace switch fully
  // disposes the old sync system and constructs a new one for the target
  // workspace's entity store.
  return (
    <>
      <AuthInitializer />
      <WorkspaceBootstrap />
      <BootGate>
        <CompanionLayer />
        <SyncSystemProvider key={activeWorkspaceId ?? 'placeholder'} workspaceId={activeWorkspaceId!} wsUrl={WS_URL}>
          <WorkspaceServiceProvider>
            <SnapshotServiceProvider>
              <CanvasPreviewProvider renderer={CanvasViewerPreview}>
              <DocumentViewerProvider renderer={EntityDocumentViewer}>
              <DocumentEditorProvider renderer={RichTextEditor}>
              <MarkdownPreviewProvider renderer={MarkdownSectionPreview}>
              <SurfaceOrbProvider renderer={SurfaceMount}>
              <SpeakerLabelProvider
                renderer={({ recordingId, speakerId, calendarEventId, entityId, surfaceId }) => (
                  <SpeakerLabelEdit
                    recordingId={recordingId}
                    speakerId={speakerId}
                    calendarEventId={calendarEventId}
                    entityId={entityId}
                    surfaceId={surfaceId}
                  />
                )}
              >
              <EditActionTransportBridge>
              <AiComposerProvider>
              <SessionWrapper>
                <WorkspaceSubscriber />
                <AuthSyncBridge />
                <SessionPruner />
                <EntitySubscriber />
                {/* Recording is a Tauri-only surface — the controller invokes
                    Tauri commands (mic permissions, recording_start/stop/
                    discard) and the hooks it consumes (usePermissionFlow)
                    call `invoke()` on mount. Mounting on web (e.g.
                    share-link visitors) crashes the React tree with "Cannot
                    read properties of undefined (reading 'invoke')". Gate
                    at the boundary so no recording code path runs on web.
                    Also gated by SyncSystemProvider (above) because the
                    controller's <HardCapTimer> reads useEntityStore — the
                    proxy throws pre-workspace-activation. */}
                {isNative && <RecordingController />}
                {isNative && <LiveTranscriptBridge />}
                <RouterBridge />
                <DeepLinkBridge />
                <FocusDocBridge />
                <InvitationBridge />
                <MembershipBridge />
                <ReferralBridge />
                <IntercomBridge />
                <Outlet />
              </SessionWrapper>
              </AiComposerProvider>
              </EditActionTransportBridge>
              </SpeakerLabelProvider>
              </SurfaceOrbProvider>
              </MarkdownPreviewProvider>
              </DocumentEditorProvider>
              </DocumentViewerProvider>
              </CanvasPreviewProvider>
            </SnapshotServiceProvider>
          </WorkspaceServiceProvider>
        </SyncSystemProvider>
      </BootGate>
    </>
  );
}

// Shared layout: wraps /shared/* routes with share-mode sync provider.
// The share token doubles as the entity-store workspaceId — share mode uses
// NoOpPersistence so no IDB writes happen, and the entity store activation
// is just to satisfy the opSyncBridge subscription contract.
//
// The token is read from useParams with a defensive fallback so the provider
// never gets `workspaceId={undefined}` on first render (which would create
// `nula-entity-store-undefined` even under NoOpPersistence due to Zustand
// persist eagerly opening storage).
function SharedLayout() {
  const params = useParams({ strict: false }) as { token?: string };
  const token = (typeof params.token === 'string' && params.token.length > 0)
    ? params.token
    : 'share-pending';
  return (
    <SyncSystemProvider key={token} workspaceId={token} wsUrl={WS_URL} mode="share">
      <Outlet />
    </SyncSystemProvider>
  );
}

// Auth layout: wraps /auth/* routes. Kept as a layout for forward-compat
// (deep links may land here), but pre-auth routes don't need sync — the
// /auth/callback route is no longer used (Clerk SDK handles OAuth internally),
// so we don't mount SyncSystemProvider here.
function AuthLayout() {
  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

const rootRoute = createRootRoute({ component: RootLayout });

// Invitation accept route — public, handles logged-in and logged-out states
function InvitationAcceptRoute() {
  const { token } = useParams({ from: '/invitations/$token' });
  return <InvitationAccept key={token} />;
}

const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitations/$token',
  component: InvitationAcceptRoute,
});

// /join/$token — shareable invite link accept. Server emits https://nula.app/join/{token} URLs.
function JoinByTokenRoute() {
  const { token } = useParams({ from: '/join/$token' });
  return <JoinByToken key={token} token={token} />;
}

const joinByTokenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$token',
  component: JoinByTokenRoute,
});

// /r/$code — referral code share link. Same shape as /join/$token: a link
// opened by someone who may have no account yet.
function RedeemReferralRoute() {
  const { code } = useParams({ from: '/r/$code' });
  return <RedeemReferral key={code} code={code} />;
}

const redeemReferralRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/r/$code',
  component: RedeemReferralRoute,
});

// /auth/callback — OAuth completion landing. Registered as the Clerk OAuth
// Application redirect URI. After Clerk completes sign-in, the system
// browser lands here, the page deep-links to nula://callback (with the
// original query params), and attempts to close the tab.
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallback,
});

// /auth/desktop — desktop OAuth handoff. Auth0's redirect_uri for the desktop
// production build. The system browser lands here, the page forwards
// code+state to nula:// (handing control to the running Tauri app) and shows a
// Nula confirmation. See apps/lumen/src/app/routes/auth-desktop.tsx.
const authDesktopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/desktop',
  component: AuthDesktop,
});

// /calendar/connected — desktop calendar OAuth handoff. The calendar callback
// redirects the system browser here so the user lands on a Nula page rather
// than the provider's consent screen. See routes/calendar-connected.tsx.
const calendarConnectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/calendar/connected',
  component: CalendarConnected,
});

// Shared routes — get SyncSystemProvider mode="share"
const sharedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shared',
  component: SharedLayout,
});

const sharedRoute = createRoute({
  getParentRoute: () => sharedLayoutRoute,
  path: '/$token',
  component: SharedRoute,
});

// Auth routes — layout route kept for forward-compat (deep links may land here),
// but /auth/callback is no longer used: Clerk SDK handles OAuth internally.
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthLayout,
});

// Main layout route — pathless, wraps all authenticated app routes
const mainLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'main',
  component: MainLayout,
});

// / redirects to /$workspaceId when bootstrapped (activeWorkspaceId persisted
// from localStorage via useWorkspaceStore's persist middleware). If no workspace
// is persisted yet (first login, not yet bootstrapped), render App and let
// WorkspaceBootstrap select one asynchronously.
const homeRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/',
  beforeLoad: () => {
    const workspaceId = useWorkspaceStore.getState().activeWorkspaceId;
    if (workspaceId) {
      throw redirect({ to: '/$workspaceId', params: { workspaceId } });
    }
  },
  component: App,
});

// /home redirects to / for clean URLs
const homeRedirectRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/home',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});

/// Helper: redirect target for all deep-link routes — always strip to root.
function legacyRedirectTarget(): { to: '/' } {
  return { to: '/' };
}

// Deep link routes — use beforeLoad interceptors to call sessionOpenTab,
// then redirect to / or /$workspaceId for clean URLs.
// In-app navigation never touches these routes.
const documentsRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/documents',
  beforeLoad: () => {
    sessionOpenTab('documents', 'view');
    throw redirect(legacyRedirectTarget());
  },
  component: () => null, // Never rendered (beforeLoad redirects)
});

const collectionsRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/collections',
  beforeLoad: () => {
    sessionOpenTab('collections', 'view');
    throw redirect(legacyRedirectTarget());
  },
  component: () => null, // Never rendered (beforeLoad redirects)
});

const trashRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/trash',
  beforeLoad: () => {
    sessionOpenTab('trash', 'view');
    throw redirect(legacyRedirectTarget());
  },
  component: () => null, // Never rendered (beforeLoad redirects)
});

const calendarRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/calendar',
  beforeLoad: () => {
    sessionOpenTab('calendar', 'view');
    throw redirect(legacyRedirectTarget());
  },
  component: () => null, // Never rendered (beforeLoad redirects)
});

// Catch-all route — renders App for any unmatched paths
const catchAllRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '$',
  component: App,
});

// ---------------------------------------------------------------------------
// Workspace-prefixed route group: /$workspaceId/...
// ---------------------------------------------------------------------------

// Parent route for workspace context — renders nothing, passes through to child routes via Outlet.
const workspaceLayoutRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/$workspaceId',
});

// /$workspaceId/ — home for a given workspace
const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/',
  component: App,
});

// /$workspaceId/calendar
const wsCalendarRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/calendar',
  beforeLoad: () => {
    sessionOpenTab('calendar', 'view');
    throw redirect({ to: '/' });
  },
  component: () => null,
});

// /$workspaceId/trash
const wsTrashRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/trash',
  beforeLoad: () => {
    sessionOpenTab('trash', 'view');
    throw redirect({ to: '/' });
  },
  component: () => null,
});

// /$workspaceId/documents
const wsDocumentsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/documents',
  beforeLoad: () => {
    sessionOpenTab('documents', 'view');
    throw redirect({ to: '/' });
  },
  component: () => null,
});

// /$workspaceId/collections
const wsCollectionsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/collections',
  beforeLoad: () => {
    sessionOpenTab('collections', 'view');
    throw redirect({ to: '/' });
  },
  component: () => null,
});

// /$workspaceId/:entityId — generic entity deep-link (any kind).
// UUID validation prevents shadowing of static workspace routes.
// Static sub-routes (calendar, trash, documents, collections) are listed
// before this route in the children array and are matched first.
//
// Validation happens inside beforeLoad rather than via params.parse: a thrown
// Error in parse is silently swallowed by TanStack Router and the beforeLoad
// runs anyway, which would forward a non-UUID slug to sessionOpenTab and
// stash a junk tab. Redirecting to '/' inside beforeLoad short-circuits cleanly.
const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const wsEntityRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/$entityId',
  beforeLoad: ({ params }) => {
    if (!UUID_REGEX.test(params.entityId)) {
      // Non-UUID slug — strip to root rather than open a garbage tab.
      throw redirect({ to: '/' });
    }
    // Pattern: beforeLoad → sessionOpenTab → throw redirect.
    const kind = resolveEntityKind(params.entityId);
    sessionOpenTab(params.entityId, kindToTabType(kind));
    throw redirect({ to: '/' });
  },
  component: () => null,
});

export const routeTree = rootRoute.addChildren([
  invitationAcceptRoute,
  joinByTokenRoute,
  redeemReferralRoute,
  authCallbackRoute,
  authDesktopRoute,
  calendarConnectedRoute,
  sharedLayoutRoute.addChildren([sharedRoute]),
  authLayoutRoute,
  mainLayoutRoute.addChildren([
    homeRoute,
    homeRedirectRoute,
    documentsRoute,
    collectionsRoute,
    calendarRoute,
    trashRoute,
    workspaceLayoutRoute.addChildren([
      workspaceHomeRoute,
      wsCalendarRoute,
      wsTrashRoute,
      wsDocumentsRoute,
      wsCollectionsRoute,
      wsEntityRoute,  // must be after static routes — UUID validation is primary guard
    ]),
    catchAllRoute,
  ]),
]);

// ---------------------------------------------------------------------------
// Router instance (exported for tests + navigation hooks)
// ---------------------------------------------------------------------------

export const router = createRouter({
  routeTree,
  history: createBrowserHistory(),
});

subscribePostHogPageview(router);

// ---------------------------------------------------------------------------
// AppRouter — entry point rendered by main.tsx
// ---------------------------------------------------------------------------

export function AppRouter() {
  return (
    <AppProvider>
      <RouterProvider router={router} />
    </AppProvider>
  );
}
